/**
 * Fleet hooks — trucks / trailers / yards CRUD + driver edit (Quick 260616-flt / DEC-067).
 *
 * Mirrors features/management/hooks.ts. List hooks request `?includeInactive=1` so
 * the Fleet console shows deactivated rows (dimmed) for reactivation. All mutations
 * hit MANAGE_FLEET-gated, tenant-scoped (RLS) endpoints; the server is the
 * authoritative gate (the Fleet nav is hidden for non-manager roles as UX only).
 *
 * Yard + driver mutations also invalidate the dispatch-board roster/yard caches so
 * the board tabs + driver grid reflect changes immediately.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery, useApiMutation, useApiOpts } from '@lodex-pro/web-shared';
import { YARDS_QUERY_KEY } from '../board/use-yards';
import { DRIVERS_QUERY_KEY } from '../jobs/use-drivers';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TruckSummary {
  readonly id: string;
  readonly unitNumber: string;
  readonly truckType: string | null;
  /** QuickBooks Class path, verbatim (migration 0085). NULL = no class. */
  readonly accountingClass: string | null;
  readonly tankCapacityBbl: number | null;
  readonly licensePlate: string | null;
  readonly licenseState: string | null;
  readonly dotNumber: string | null;
  readonly registrationExpiry: string | null;
  readonly homeYardId: string | null;
  readonly homeYardName: string | null;
  readonly defaultDriverId: string | null;
  readonly defaultDriverName: string | null;
  readonly defaultTrailerId: string | null;
  readonly defaultTrailerUnit: string | null;
  readonly isActive: boolean;
}
export interface TrailerSummary {
  readonly id: string;
  readonly unitNumber: string;
  readonly trailerType: string | null;
  readonly capacityBbl: number | null;
  readonly homeYardId: string | null;
  readonly homeYardName: string | null;
  readonly assignedTruckId: string | null;
  readonly assignedTruckUnit: string | null;
  readonly isActive: boolean;
}
export interface YardSummary {
  readonly id: string;
  readonly name: string;
  readonly address: string | null;
  readonly gpsLat: number | null;
  readonly gpsLng: number | null;
  readonly geofenceRadiusFt: number;
  readonly isActive: boolean;
}
export interface FleetDriver {
  readonly driverId: string;
  readonly driverRowId: string | null;
  readonly driverName: string;
  readonly shift: string;
  readonly homeYardId: string | null;
  readonly yardName: string | null;
  readonly cdlNumber: string | null;
  readonly cdlState: string | null;
  readonly cdlExpiresAt: string | null;
  /** Canonical E.164, or null when the caller lacks MANAGE_USERS. */
  readonly phone: string | null;
}

export const FLEET_TRUCKS_KEY = ['trucks', 'fleet'] as const;
export const FLEET_TRAILERS_KEY = ['trailers', 'fleet'] as const;
export const FLEET_YARDS_KEY = ['yards', 'fleet'] as const;
export const FLEET_DRIVERS_KEY = ['drivers', 'fleet'] as const;

// ─── Mutation bodies ─────────────────────────────────────────────────────────

export interface TruckBody {
  unitNumber?: string;
  truckType?: string | null;
  accountingClass?: string | null;
  tankCapacityBbl?: number | null;
  licensePlate?: string | null;
  licenseState?: string | null;
  dotNumber?: string | null;
  registrationExpiry?: string | null;
  homeYardId?: string | null;
  defaultDriverId?: string | null;
  defaultTrailerId?: string | null;
  isActive?: boolean;
}
export interface TrailerBody {
  unitNumber?: string;
  trailerType?: string | null;
  capacityBbl?: number | null;
  homeYardId?: string | null;
  assignedTruckId?: string | null;
  isActive?: boolean;
}
export interface YardBody {
  name?: string;
  address?: string | null;
  gpsLat?: number | null;
  gpsLng?: number | null;
  geofenceRadiusFt?: number;
  isActive?: boolean;
}
export interface DriverBody {
  shift?: string;
  homeYardId?: string | null;
  cdlNumber?: string | null;
  cdlState?: string | null;
  cdlExpiresAt?: string | null;
  /** Plain 10-digit US input — the server canonicalizes. Never a "+1". */
  phone?: string;
}

// ─── List hooks (include inactive) ───────────────────────────────────────────

export function useFleetTrucks(): ReturnType<
  typeof useApiQuery<{ trucks: readonly TruckSummary[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ trucks: readonly TruckSummary[] }>(opts, '/v1/trucks?includeInactive=1', {
    queryKey: FLEET_TRUCKS_KEY,
    staleTime: 30_000,
  });
}

export function useFleetTrailers(): ReturnType<
  typeof useApiQuery<{ trailers: readonly TrailerSummary[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ trailers: readonly TrailerSummary[] }>(
    opts,
    '/v1/trailers?includeInactive=1',
    { queryKey: FLEET_TRAILERS_KEY, staleTime: 30_000 },
  );
}

export function useFleetYards(): ReturnType<typeof useApiQuery<{ yards: readonly YardSummary[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ yards: readonly YardSummary[] }>(opts, '/v1/yards?includeInactive=1', {
    queryKey: FLEET_YARDS_KEY,
    staleTime: 30_000,
  });
}

export function useFleetDrivers(): ReturnType<
  typeof useApiQuery<{ drivers: readonly FleetDriver[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ drivers: readonly FleetDriver[] }>(opts, '/v1/drivers', {
    queryKey: FLEET_DRIVERS_KEY,
    staleTime: 30_000,
  });
}

// ─── Trucks mutations ────────────────────────────────────────────────────────

export function useCreateTruck(
  onDone: () => void,
): ReturnType<typeof useApiMutation<TruckBody, { truck: { id: string } }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<TruckBody, { truck: { id: string } }>(opts, '/v1/trucks', 'POST', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FLEET_TRUCKS_KEY });
      void qc.invalidateQueries({ queryKey: FLEET_TRAILERS_KEY });
      onDone();
    },
  });
}

export function useUpdateTruck(
  id: string,
): ReturnType<typeof useApiMutation<TruckBody, { truck: { id: string } }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<TruckBody, { truck: { id: string } }>(opts, `/v1/trucks/${id}`, 'PATCH', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FLEET_TRUCKS_KEY });
      void qc.invalidateQueries({ queryKey: FLEET_TRAILERS_KEY });
    },
  });
}

// ─── Trailers mutations ──────────────────────────────────────────────────────

export function useCreateTrailer(
  onDone: () => void,
): ReturnType<typeof useApiMutation<TrailerBody, { trailer: { id: string } }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<TrailerBody, { trailer: { id: string } }>(opts, '/v1/trailers', 'POST', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FLEET_TRAILERS_KEY });
      void qc.invalidateQueries({ queryKey: FLEET_TRUCKS_KEY });
      onDone();
    },
  });
}

export function useUpdateTrailer(
  id: string,
): ReturnType<typeof useApiMutation<TrailerBody, { trailer: { id: string } }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<TrailerBody, { trailer: { id: string } }>(
    opts,
    `/v1/trailers/${id}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: FLEET_TRAILERS_KEY });
        void qc.invalidateQueries({ queryKey: FLEET_TRUCKS_KEY });
      },
    },
  );
}

// ─── Yards mutations (also refresh the dispatch-board yard tabs) ─────────────

export function useCreateYard(
  onDone: () => void,
): ReturnType<typeof useApiMutation<YardBody, { yard: { id: string } }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<YardBody, { yard: { id: string } }>(opts, '/v1/yards', 'POST', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FLEET_YARDS_KEY });
      void qc.invalidateQueries({ queryKey: YARDS_QUERY_KEY });
      onDone();
    },
  });
}

export function useUpdateYard(
  id: string,
): ReturnType<typeof useApiMutation<YardBody, { yard: { id: string } }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<YardBody, { yard: { id: string } }>(opts, `/v1/yards/${id}`, 'PATCH', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FLEET_YARDS_KEY });
      void qc.invalidateQueries({ queryKey: YARDS_QUERY_KEY });
    },
  });
}

// ─── Driver edit (also refresh the board roster) ─────────────────────────────

export function useUpdateDriver(
  driverId: string,
): ReturnType<typeof useApiMutation<DriverBody, { driver: FleetDriver }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<DriverBody, { driver: FleetDriver }>(
    opts,
    `/v1/drivers/${driverId}`,
    'PATCH',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: FLEET_DRIVERS_KEY });
        void qc.invalidateQueries({ queryKey: DRIVERS_QUERY_KEY });
        // Phase 20.6e — the Invite confirm reads the phone from the team list;
        // without this, adding a missing phone via Edit left Invite blocked on
        // "no mobile number" until the list went stale.
        void qc.invalidateQueries({ queryKey: ['users'] });
      },
    },
  );
}
